import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Flame, Zap, Target } from "lucide-react";
import api from "@/lib/api";

/**
 * Mission Control header — reusable across any 30-Day-style roadmap (Data Cloud, SFMC,
 * MC Next, ServiceNow, Custom AI Challenges). Self-fetches the learner's live streak/XP
 * from the shared dashboard endpoint so any roadmap page can drop this in with zero props
 * besides its own day/total-days progress.
 */
export default function MissionControlHeader({ totalDays = 30, completedCount = 0 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/users/me/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const pct = totalDays ? Math.min(100, Math.round((completedCount / totalDays) * 100)) : 0;

  return (
    <div
      data-testid="mission-control-header"
      className="glass-panel rounded-2xl p-5 sm:p-6 mb-8 relative overflow-hidden border border-indigo-400/15"
    >
      <div className="absolute -top-20 -right-10 w-56 h-56 bg-indigo-500/10 rounded-full blur-[80px] pointer-events-none" />
      <div className="relative flex items-center justify-between flex-wrap gap-6">
        <div className="flex items-center gap-6 sm:gap-8">
          <div className="flex items-center gap-3" data-testid="mission-control-streak">
            <motion.div
              animate={{ boxShadow: ["0 0 0px rgba(255,138,61,0.3)", "0 0 22px rgba(255,138,61,0.55)", "0 0 0px rgba(255,138,61,0.3)"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-12 h-12 rounded-2xl bg-[#ff8a3d]/10 border border-[#ff8a3d]/30 flex items-center justify-center shrink-0"
            >
              <Flame className="w-6 h-6 text-[#ff8a3d]" />
            </motion.div>
            <div>
              <p className="font-mono text-2xl font-bold text-white tabular-nums">
                <CountUp end={data?.streak_current ?? 0} duration={1.2} />
                <span className="text-sm text-slate-400 font-sans ml-1">day{data?.streak_current === 1 ? "" : "s"}</span>
              </p>
              <p className="text-[11px] uppercase tracking-wider text-slate-400">Mission Streak</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="mission-control-xp">
            <div className="w-12 h-12 rounded-2xl bg-[#FBBF24]/10 border border-[#FBBF24]/30 flex items-center justify-center shrink-0">
              <Zap className="w-6 h-6 text-[#FBBF24]" />
            </div>
            <div>
              <p className="font-mono text-2xl font-bold text-white tabular-nums">
                <CountUp end={data?.xp ?? 0} duration={1.2} separator="," />
              </p>
              <p className="text-[11px] uppercase tracking-wider text-slate-400">Total XP</p>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[220px]" data-testid="mission-control-progress">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
            <span className="flex items-center gap-1.5 font-semibold text-slate-300"><Target className="w-3.5 h-3.5 text-indigo-400" /> Mission Progress</span>
            <span>{completedCount}/{totalDays} Days · {pct}%</span>
          </div>
          <div className="h-3 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-[#FBBF24] rounded-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
